<script>
import { onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Spinner from 'next/spinner/Spinner.vue';
import Sidebar from './components/sidebar/Index.vue';
import Trigger from './components/trigger/Index.vue';
import useVuelidate from '@vuelidate/core';
import { mapGetters } from 'vuex';

import { isStepMoveInProgress } from 'dashboard/store/utils/wgptAutomationBuilderUtils';

import {
  findStep,
  findStepAndDelete,
  findStepAndUpdate,
  getStepPath,
  computeStepVariablesMap,
  getFieldVariables,
} from './helpers/automationUtils';

export default {
  components: {
    Trigger,
    Sidebar,
    Spinner,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    disableTrigger: {
      type: Boolean,
      default: false,
    },
    disableTriggerAttributes: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props) {
    const { t } = useI18n();

    const onBeforeUnload = e => {
      if (props.dirty) {
        e.preventDefault();
      }
    };

    onMounted(() => {
      window.addEventListener('beforeunload', onBeforeUnload);
    });

    onUnmounted(() => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    });

    onBeforeRouteLeave(() => {
      if (props.dirty) {
        // eslint-disable-next-line no-alert
        const answer = window.confirm(
          t('WGPT_AUTOMATIONS.EDITOR.UNSAVED_CHANGES_CONFIRM')
        );
        if (!answer) return false;
      }
      return true;
    });

    return { v$: useVuelidate() };
  },
  data() {
    return {
      automation: {
        ...this.modelValue,
        name: this.modelValue.name,
        description: this.modelValue.description,
        steps: this.modelValue.steps,
        trigger: this.modelValue.trigger,
      },
      showActionsDropdown: false,
      readyToRender: false,
    };
  },
  computed: {
    ...mapGetters({
      actionsRegistry: 'wgptAutomationRegistry/getActions',
      getCustomObjects: 'wgptAutomationRegistry/getCustomObjects',
      getDynamicOptions: 'wgptAutomationRegistry/getDynamicOptions',
    }),
    automationId() {
      return this.modelValue.id;
    },
    trigger() {
      return this.automation.trigger || {};
    },
    triggerType() {
      return this.trigger.type;
    },
    triggerRegistry() {
      return this.$store.getters['wgptAutomationRegistry/getTrigger'](
        this.triggerType
      );
    },
    selectedStepId() {
      return this.$route.query.selected;
    },
    triggerVariables() {
      const trigger = this.triggerRegistry || {};
      const availableDataFields = trigger.available_data || {};
      const availableDataVariables = Object.entries(availableDataFields).map(
        ([name, field]) => {
          return { ...field, name };
        }
      );
      const inputMapping = this.trigger.input_mapping || {};
      const inputMappingFields = trigger.input_mapping_fields || [];
      const inputMappingVariables = getFieldVariables(
        inputMapping,
        inputMappingFields
      );
      return [...availableDataVariables, ...inputMappingVariables];
    },
    stepVariablesMap() {
      const steps = this.automation.steps?.v || [];
      return computeStepVariablesMap(
        steps,
        this.actionsRegistry,
        this.triggerVariables
      );
    },
    variables() {
      if (this.selectedStepId) {
        return (
          this.stepVariablesMap.get(this.selectedStepId) ||
          this.triggerVariables
        );
      }
      return this.triggerVariables;
    },
    isEditorReady() {
      if (!this.readyToRender) return false;
      if (!this.triggerType) return true;
      if (!this.triggerRegistry) return false;
      if (this.actionsRegistry.length === 0) return false;
      return true;
    },
    disableTriggerChange() {
      if (this.disableTrigger) return true;
      if (!this.trigger.type) return false;
      const steps = this.automation.steps.v || [];
      return steps.length > 0;
    },
  },
  watch: {
    variables: {
      handler(variables) {
        this.dispatchWrappers(variables);
      },
      immediate: true,
    },
    triggerType: {
      handler(value) {
        if (!value) return;
        this.$store.dispatch('wgptAutomationRegistry/getActions', value);
      },
      immediate: true,
    },
    triggerRegistry(registry) {
      if (!registry) return;
      this.cleanTriggerStaleData(registry);
    },
    modelValue: {
      handler(modelValue) {
        if (!modelValue) return;
        if (this.isSyncingToParent) return;
        this.automation = {
          ...modelValue,
          name: modelValue.name,
          description: modelValue.description,
          steps: { ...modelValue.steps },
          trigger: { ...modelValue.trigger },
        };
        this.$store.dispatch('wgptAutomationBuilder/set', this.automation);
      },
      immediate: true,
    },
  },
  validations: {
    automation: {},
  },
  created() {
    this.isSyncingToParent = false;
  },
  mounted() {
    this.setDefaults();
    this.validateSelectedStep();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.readyToRender = true;
      });
    });
  },
  methods: {
    setDefaults() {
      this.$store.dispatch('wgptAutomationRegistry/getTriggers');
      this.$store.dispatch('wgptAutomationRegistry/getLogicalOperators');
      this.$store.dispatch('wgptAutomationRegistry/getComparisonOperators');
    },
    validateSelectedStep() {
      const stepId = this.$route.query.selected;
      if (!stepId || stepId === 'settings') return;
      const steps = this.automation.steps?.v || [];
      if (!findStep(steps, stepId)) {
        const { selected, ...query } = this.$route.query;
        this.$router.replace({ query }).catch(() => {});
      }
    },
    syncAutomation() {
      this.isSyncingToParent = true;
      this.$emit('update:modelValue', this.automation);
      this.$store.dispatch('wgptAutomationBuilder/set', this.automation);
      this.$nextTick(() => {
        this.isSyncingToParent = false;
      });
    },
    onTriggerChange(triggerUpdates) {
      const triggerBefore = this.automation.trigger || {};
      this.automation.trigger = {
        ...triggerBefore,
        ...triggerUpdates,
      };
      this.syncAutomation();
    },
    onSettingsChange(automationUpdates) {
      Object.keys(automationUpdates).forEach(key => {
        this.automation[key] = automationUpdates[key];
      });
      this.syncAutomation();
    },
    onStepUpdate(stepId, data) {
      try {
        const stepsBefore = this.automation.steps.v || [];
        const steps = stepId
          ? findStepAndUpdate(stepsBefore, stepId, data)
          : data;

        this.automation.steps = {
          v: steps,
        };
        this.syncAutomation();
      } catch (error) {
        const errorMessage = error.message || this.$t('WGPT_AUTOMATIONS');
        useAlert(errorMessage);
      }
    },
    onStepDelete(stepId) {
      try {
        const stepsBefore = this.automation.steps.v || [];
        const selectedStepPath =
          getStepPath(stepsBefore, this.selectedStepId) || [];
        const steps = findStepAndDelete(stepsBefore, stepId);

        // Skip navigation when a drag-move is in progress — the drop
        // handler will navigate to the new step ID instead.
        if (!isStepMoveInProgress()) {
          for (let i = selectedStepPath.length; i >= 0; i -= 1) {
            const id = selectedStepPath[i];
            if (findStep(steps, id)) {
              this.$router.replace({ query: { selected: id } }).catch(() => {});
              break;
            }
            if (i === 0) {
              this.$router.replace({ query: {} }).catch(() => {});
            }
          }
        }

        this.automation.steps = {
          v: steps,
        };
        this.syncAutomation();
      } catch (error) {
        const errorMessage = error.message || this.$t('WGPT_AUTOMATIONS');
        useAlert(errorMessage);
      }
    },

    cleanTriggerStaleData(registry) {
      const trigger = this.automation.trigger;
      if (!trigger) return;

      const formFieldNames = (registry.form_fields || []).map(f => f.name);
      const inputMappingFieldNames = (registry.input_mapping_fields || []).map(
        f => f.name
      );

      let changed = false;

      if (trigger.attributes) {
        const cleaned = {};
        formFieldNames.forEach(name => {
          if (name in trigger.attributes) {
            cleaned[name] = trigger.attributes[name];
          }
        });
        if (
          Object.keys(cleaned).length !== Object.keys(trigger.attributes).length
        ) {
          trigger.attributes = cleaned;
          changed = true;
        }
      }

      if (trigger.input_mapping) {
        const cleaned = {};
        inputMappingFieldNames.forEach(name => {
          if (name in trigger.input_mapping) {
            cleaned[name] = trigger.input_mapping[name];
          }
        });
        if (
          Object.keys(cleaned).length !==
          Object.keys(trigger.input_mapping).length
        ) {
          trigger.input_mapping = cleaned;
          changed = true;
        }
      }

      if (changed) {
        this.syncAutomation();
      }
    },
    dispatchWrappers(variables) {
      variables.forEach(variable => {
        if (variable.wrapper) {
          if (variable.custom_object) {
            if (this.getCustomObjects(variable.custom_object)) return;

            this.$store.dispatch(
              'wgptAutomationRegistry/getCustomObjects',
              variable.custom_object
            );
            return;
          }
        }

        if (variable.model) {
          if (this.getDynamicOptions(variable.model)) return;

          this.$store.dispatch(
            'wgptAutomationRegistry/getDynamicOptions',
            variable.model
          );
        }
      });
    },
  },
};
</script>

<template>
  <div class="flex">
    <div
      class="automation-rule-designer flex-1 h-full overflow-auto macro-gradient-radial dark:macro-dark-gradient-radial macro-gradient-radial-size"
    >
      <Trigger
        v-if="isEditorReady"
        class="mb-[50vh] mx-20 w-max min-w-full"
        :automation="automation"
        :variables="triggerVariables"
        :disabled="disabled"
        @step-change="onStepUpdate"
        @step-delete="onStepDelete"
      />
      <div
        v-else
        class="flex items-center justify-center h-full text-n-slate-11"
      >
        <Spinner />
      </div>
    </div>
    <Sidebar
      :variables="variables"
      :automation="automation"
      :disable-trigger-change="disableTriggerChange"
      :disable-trigger-attributes="disableTriggerAttributes"
      :disabled="disabled"
      @trigger-change="onTriggerChange"
      @step-change="onStepUpdate"
      @settings-change="onSettingsChange"
    >
      <template v-if="$slots.customSidebarSettings" #customSettings>
        <slot name="customSidebarSettings" />
      </template>
    </Sidebar>
  </div>
</template>

<style scoped>
.automation-rule-designer {
  --triggerThemeColor: #fd6262;
  --actionThemeColor: #44ce4b;
  --branchThemeColor: #ffc532;
  --forEachThemeColor: #a855f7;
  --linkedThemeColor: #ff32ea;
}

@tailwind components;
@layer components {
  .macro-gradient-radial {
    background-image: radial-gradient(#ebf0f5 1.2px, transparent 0);
  }
  .macro-dark-gradient-radial {
    background-image: radial-gradient(#293f51 1.2px, transparent 0);
  }
  .macro-gradient-radial-size {
    background-size: 1rem 1rem;
  }
}
</style>
